import React, { useState, useEffect } from 'react';
import { Users, Building2, DollarSign, TrendingUp, Clock, CheckCircle, MessageSquare, Mail, Zap } from 'lucide-react';
import { supabase } from '../supabase';
import { formatCurrency, getCurrentPeriodMonth, getMonthLabel } from '../utils/commissionCalc';

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalAffiliates: 0,
    activeAffiliates: 0,
    totalAccounts: 0,
    attributedAccounts: 0,
    commissionsOwed: 0,
    commissionsPaid: 0,
    recentSignups: []
  });
  const [messagingStats, setMessagingStats] = useState({
    totalSubscriptions: 0,
    activeSubscriptions: 0,
    totalMessagesThisMonth: 0,
    smsThisMonth: 0,
    emailThisMonth: 0,
    revenue: 0
  });

  const currentMonth = getCurrentPeriodMonth();

  useEffect(() => {
    loadDashboardData();
    loadMessagingStats();
  }, []);

  const loadDashboardData = async () => {
    try {
      // Get affiliate counts
      const { data: affiliates } = await supabase
        .from('affiliates')
        .select('id, active');

      // Get company counts with attribution
      const { data: companies } = await supabase
        .from('companies')
        .select('id, name, referred_by_affiliate_id, created_at, subscription_status')
        .order('created_at', { ascending: false });

      // Get current month commissions
      const { data: commissions } = await supabase
        .from('affiliate_commissions')
        .select('*')
        .eq('period_month', currentMonth);

      const activeAffiliates = affiliates?.filter(a => a.active).length || 0;
      const attributedAccounts = companies?.filter(c => c.referred_by_affiliate_id).length || 0;
      const owedCommissions = commissions?.filter(c => c.status === 'owed').reduce((sum, c) => sum + parseFloat(c.commission_amount), 0) || 0;
      const paidCommissions = commissions?.filter(c => c.status === 'paid').reduce((sum, c) => sum + parseFloat(c.commission_amount), 0) || 0;

      setStats({
        totalAffiliates: affiliates?.length || 0,
        activeAffiliates,
        totalAccounts: companies?.length || 0,
        attributedAccounts,
        commissionsOwed: owedCommissions,
        commissionsPaid: paidCommissions,
        recentSignups: companies?.slice(0, 5) || []
      });
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessagingStats = async () => {
    try {
      // Get messaging subscriptions
      const { data: subscriptions } = await supabase
        .from('user_messaging_subscriptions')
        .select('*');

      // Get this month's message usage
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      const { data: usage } = await supabase
        .from('message_usage')
        .select('message_type, credits_used')
        .gte('sent_at', startOfMonth.toISOString());

      const activeSubscriptions = subscriptions?.filter(s => s.status === 'active').length || 0;
      const smsCount = usage?.filter(u => u.message_type === 'sms').length || 0;
      const emailCount = usage?.filter(u => u.message_type === 'email').length || 0;

      // Calculate estimated revenue
      // Weekly: $4, Monthly: $16, Annual: $160
      const weeklyRev = (subscriptions?.filter(s => s.status === 'active' && s.plan_type === 'weekly').length || 0) * 4;
      const monthlyRev = (subscriptions?.filter(s => s.status === 'active' && s.plan_type === 'monthly').length || 0) * 16;
      const annualRev = (subscriptions?.filter(s => s.status === 'active' && s.plan_type === 'annual').length || 0) * (160 / 12); // Monthly portion

      setMessagingStats({
        totalSubscriptions: subscriptions?.length || 0,
        activeSubscriptions,
        totalMessagesThisMonth: smsCount + emailCount,
        smsThisMonth: smsCount,
        emailThisMonth: emailCount,
        revenue: weeklyRev + monthlyRev + annualRev
      });
    } catch (error) {
      console.error('Error loading messaging stats:', error);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#888' }}>
        Loading dashboard...
      </div>
    );
  }

  const statCards = [
    {
      label: 'Active Affiliates',
      value: stats.activeAffiliates,
      subtext: `${stats.totalAffiliates} total`,
      icon: Users,
      color: '#3498db'
    },
    {
      label: 'Attributed Accounts',
      value: stats.attributedAccounts,
      subtext: `of ${stats.totalAccounts} total`,
      icon: Building2,
      color: '#9b59b6'
    },
    {
      label: 'Owed This Month',
      value: formatCurrency(stats.commissionsOwed),
      subtext: getMonthLabel(currentMonth),
      icon: Clock,
      color: '#f39c12'
    },
    {
      label: 'Paid This Month',
      value: formatCurrency(stats.commissionsPaid),
      subtext: getMonthLabel(currentMonth),
      icon: CheckCircle,
      color: '#4ecca3'
    }
  ];

  return (
    <div style={{ padding: '2rem 3rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{
          fontSize: '2rem',
          fontWeight: '700',
          color: '#e0e0e0',
          marginBottom: '0.5rem'
        }}>
          Dashboard
        </h1>
        <p style={{ color: '#888' }}>
          Overview of affiliate performance and commissions
        </p>
      </div>

      {/* Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '1.5rem',
        marginBottom: '3rem'
      }}>
        {statCards.map((stat, idx) => {
          const Icon = stat.icon;
          return (
            <div
              key={idx}
              style={{
                background: '#1a1a1a',
                borderRadius: '12px',
                padding: '1.5rem',
                border: '1px solid #2a2a2a',
                borderLeft: `4px solid ${stat.color}`
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '1rem'
              }}>
                <Icon size={24} color={stat.color} />
              </div>
              <div style={{
                fontSize: '2rem',
                fontWeight: '700',
                color: stat.color,
                fontFamily: '"JetBrains Mono", monospace',
                marginBottom: '0.25rem'
              }}>
                {stat.value}
              </div>
              <div style={{
                fontSize: '0.9rem',
                color: '#e0e0e0',
                fontWeight: '500'
              }}>
                {stat.label}
              </div>
              <div style={{
                fontSize: '0.8rem',
                color: '#666',
                marginTop: '0.25rem'
              }}>
                {stat.subtext}
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent Signups */}
      <div style={{
        background: '#1a1a1a',
        borderRadius: '12px',
        border: '1px solid #2a2a2a',
        padding: '1.5rem'
      }}>
        <h2 style={{
          fontSize: '1.25rem',
          fontWeight: '700',
          color: '#e0e0e0',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <TrendingUp size={20} color="#ff6b35" />
          Recent Signups
        </h2>

        {stats.recentSignups.length === 0 ? (
          <p style={{ color: '#666', textAlign: 'center', padding: '2rem' }}>
            No accounts yet
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {stats.recentSignups.map(company => (
              <div
                key={company.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '1rem',
                  background: '#242424',
                  borderRadius: '8px'
                }}
              >
                <div>
                  <div style={{ fontWeight: '600', color: '#e0e0e0' }}>
                    {company.name}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#888' }}>
                    {new Date(company.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{
                    padding: '0.25rem 0.75rem',
                    background: company.referred_by_affiliate_id ? '#4ecca320' : '#3a3a3a',
                    color: company.referred_by_affiliate_id ? '#4ecca3' : '#888',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: '600'
                  }}>
                    {company.referred_by_affiliate_id ? 'Attributed' : 'Organic'}
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#666',
                    marginTop: '0.25rem',
                    textTransform: 'capitalize'
                  }}>
                    {company.subscription_status}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Messaging Stats Section */}
      <div style={{
        background: '#1a1a1a',
        borderRadius: '12px',
        border: '1px solid #2a2a2a',
        padding: '1.5rem',
        marginTop: '2rem'
      }}>
        <h2 style={{
          fontSize: '1.25rem',
          fontWeight: '700',
          color: '#e0e0e0',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <MessageSquare size={20} color="#6366f1" />
          Messaging Addon Performance
        </h2>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '1rem'
        }}>
          {/* Active Subscriptions */}
          <div style={{
            background: '#242424',
            borderRadius: '8px',
            padding: '1.25rem',
            borderLeft: '3px solid #6366f1'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <Zap size={16} color="#6366f1" />
              <span style={{ color: '#888', fontSize: '0.85rem' }}>Active Subs</span>
            </div>
            <div style={{
              fontSize: '1.75rem',
              fontWeight: '700',
              color: '#6366f1',
              fontFamily: '"JetBrains Mono", monospace'
            }}>
              {messagingStats.activeSubscriptions}
            </div>
            <div style={{ color: '#666', fontSize: '0.75rem' }}>
              {messagingStats.totalSubscriptions} total
            </div>
          </div>

          {/* Monthly Revenue */}
          <div style={{
            background: '#242424',
            borderRadius: '8px',
            padding: '1.25rem',
            borderLeft: '3px solid #22c55e'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <DollarSign size={16} color="#22c55e" />
              <span style={{ color: '#888', fontSize: '0.85rem' }}>MRR</span>
            </div>
            <div style={{
              fontSize: '1.75rem',
              fontWeight: '700',
              color: '#22c55e',
              fontFamily: '"JetBrains Mono", monospace'
            }}>
              {formatCurrency(messagingStats.revenue)}
            </div>
            <div style={{ color: '#666', fontSize: '0.75rem' }}>
              from messaging
            </div>
          </div>

          {/* SMS This Month */}
          <div style={{
            background: '#242424',
            borderRadius: '8px',
            padding: '1.25rem',
            borderLeft: '3px solid #3b82f6'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <MessageSquare size={16} color="#3b82f6" />
              <span style={{ color: '#888', fontSize: '0.85rem' }}>SMS Sent</span>
            </div>
            <div style={{
              fontSize: '1.75rem',
              fontWeight: '700',
              color: '#3b82f6',
              fontFamily: '"JetBrains Mono", monospace'
            }}>
              {messagingStats.smsThisMonth.toLocaleString()}
            </div>
            <div style={{ color: '#666', fontSize: '0.75rem' }}>
              this month
            </div>
          </div>

          {/* Emails This Month */}
          <div style={{
            background: '#242424',
            borderRadius: '8px',
            padding: '1.25rem',
            borderLeft: '3px solid #f59e0b'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <Mail size={16} color="#f59e0b" />
              <span style={{ color: '#888', fontSize: '0.85rem' }}>Emails Sent</span>
            </div>
            <div style={{
              fontSize: '1.75rem',
              fontWeight: '700',
              color: '#f59e0b',
              fontFamily: '"JetBrains Mono", monospace'
            }}>
              {messagingStats.emailThisMonth.toLocaleString()}
            </div>
            <div style={{ color: '#666', fontSize: '0.75rem' }}>
              this month
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
